<template>
  <div class="d-flex flex-column w-100" style="overflow-x: hidden">
    <DashboardFilters
      :date-range="[startDate, endDate]"
      :time-zone="selectedTimezone"
      :auto-refresh="sellerAutoRefresh"
      :toggle-auto-refresh="TOGGLE_SELLER_AUTO_REFRESH"
      @date-range-updated="reloadData"
      @update-time-zone="updateTimezone"
      :refresh="loadAllData"
    />
    <div class="mb-4">
      <b-row no-gutters>
        <b-col md="3" class="pr-2">
          <DashboardInsights
            v-if="Object.keys(sellerDashboardInsights).length > 0"
            :insights="sellerDashboardInsights"
            account-type="seller"
            class="h-100"
          />
        </b-col>
        <b-col md="9">
          <DashboardGraph
            :chart-data="chartData"
            class="h-100"
          />
        </b-col>
      </b-row>
    </div>
    <div>
      <SellerDashboardOffers
        :offers="mergedOffers"
      />
    </div>
  </div>
</template>

<script>
import DashboardFilters from '@/components/DashboardFilters.vue'
import DashboardInsights from '@/components/DashboardInsights.vue'
import DashboardGraph from '@/components/DashboardGraph.vue'
import SellerDashboardOffers from './SellerDashboardOffers.vue'
import { mapState, mapActions, mapMutations, mapGetters } from 'vuex'
import moment from 'moment-timezone'

const AUTO_RELOAD_INTERVAL = 120 * 1000 // 2 mins interval

let autoReloadTimer

export default {
  name: 'SellerDashboard',

  title: 'Dashboard',

  components: {
    DashboardFilters,
    DashboardInsights,
    DashboardGraph,
    SellerDashboardOffers
  },

  data() {
    return {
      startDate: null,
      endDate: null,
      selectedTimezone: 'UTC',
      useMockData: false // Flag to toggle mock data
    }
  },

  computed: {
    ...mapState('dashboard', [
      'sellerDashboardInsights',
      'sellerDashboardOffers',
      'sellerAutoRefresh',
      'sellerDashboardGraphData' // This will hold our raw records
    ]),

    ...mapState('auth', ['accountDetails']),
    ...mapGetters('auth', ['accountId']),

    defaultStartDate() {
      return moment.utc().startOf('month').toDate()
    },
    defaultEndDate() {
      return moment.utc().endOf('month').toDate()
    },
    chartData() {
      const records = this.sellerDashboardGraphData || []
      
      // Ensure we have datasets array even if empty
      // DashboardGraph / Chart.js requires { labels, datasets } or just datasets with x/y
      
      const categories = {}

      records.forEach(r => {
        const cat = r.categoryName || 'Unknown'
        // timestamp from r.date (ISO) or datesByYearMonthDay
        const dateStr = r.date || r.datesByYearMonthDay
        if (!dateStr) return

        if (!categories[cat]) {
          categories[cat] = []
        }
        
        categories[cat].push({
          x: new Date(dateStr),
          y: r.earningsPerBillableCallGross || 0
        })
      })

      const sortedCategories = Object.keys(categories).sort((a, b) => {
        const sumA = categories[a].reduce((sum, p) => sum + p.y, 0)
        const sumB = categories[b].reduce((sum, p) => sum + p.y, 0)
        return sumB - sumA
      })

      const topCategories = sortedCategories.slice(0, 10)

      const colors = [
        '#321fdb', '#e55353', '#f9b115', '#2eb85c', '#3399ff', 
        '#6f42c1', '#20c997', '#ffc107', '#17a2b8', '#6610f2'
      ]

      const datasets = topCategories.map((cat, index) => {
        const color = colors[index % colors.length]
        return {
          type: 'line',
          label: cat,
          borderColor: color,
          backgroundColor: 'transparent',
          borderWidth: 2,
          pointBackgroundColor: color,
          pointBorderColor: '#fff',
          pointRadius: 4,
          fill: false,
          tension: 0, 
          data: categories[cat].sort((a,b) => a.x - b.x)
        }
      })

      return {
        datasets
      }
    },
    mergedOffers() {
      return this.sellerDashboardOffers || []
    }
  },
  watch: {
    sellerAutoRefresh (newValue) {
      if (newValue) {
        autoReloadTimer = setInterval(async () => {
          await this.loadAllData()
        }, AUTO_RELOAD_INTERVAL)
      } else {
        this.clearAutoReloadInterval()
      }
    }
  },
  async created() {
    this.startDate = this.defaultStartDate
    this.endDate = this.defaultEndDate

    await this.loadAllData()
  },
  methods: {
    ...mapActions('dashboard', ['getSellerOffers', 'getSellerDashboardData']),
    ...mapMutations('dashboard', ['TOGGLE_SELLER_AUTO_REFRESH', 'SET_SELLER_DASHBOARD_GRAPH_DATA', 'SET_SELLER_DASHBOARD_INSIGHTS']),
    
    async reloadData([ from, to ]) {
      this.startDate = from
      this.endDate = to
      await this.loadAllData()
    },

    async updateTimezone(timezone) {
      this.selectedTimezone = timezone
      // Re-fetch data if timezone affects backend query.
      // Currently API takes ISO start/end, which are usually UTC.
      // If the backend respects the timezone param or if we need to adjust start/end to be relative to that timezone,
      // we might need logic here. For now, just setting it.
      // Ideally, start/end dates selected are already in local time of the picker, which handles timezone logic?
      // DashbaordFilters just passes dates.
      await this.loadAllData()
    },

    async loadAllData() {
      if (!this.startDate || !this.endDate) return

      await this.getSellerDashboardData({
        from: this.startDate, 
        to: this.endDate,
        timezone: this.selectedTimezone
      })
    },



    clearAutoReloadInterval() {
      if (autoReloadTimer) {
        clearInterval(autoReloadTimer)
        autoReloadTimer = null
      }
    }
  }
}
</script>

<style lang="scss" scoped>
::v-deep .panel {
  margin: 0;
  height: 100%;
}
</style>
